import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ArrowRight, Loader2, Menu, X, LayoutDashboard } from 'lucide-react';
import {
  FaInstagram, FaXTwitter, FaLinkedin, FaFacebook, FaTiktok,
  FaYoutube, FaThreads, FaPinterest, FaBluesky,
} from 'react-icons/fa6';
import type { IconType } from 'react-icons';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { getFeaturesByCategory } from '../lib/features';
import { TOOLS } from '../lib/tools';

type BlogPostCard = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  category: string;
  publishedAt: string | null;
};

export function Navbar() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu whenever the route changes (also handles in-page anchors).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header className="bg-white/80 backdrop-blur border-b border-slate-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-8">
        {/* Logo + Nav grouped on the left */}
        <div className="flex items-center gap-10">
          <Link to="/" className="flex items-center">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-8 w-auto" />
          </Link>

          {/* Nav central — desktop only */}
          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium">
            <SocialMenu />
            <FeaturesMenu />
            <ToolsMenu />
            <Link to="/precios" className="text-slate-600 hover:text-slate-900 transition-colors">Precios</Link>
            <BlogMenu />
          </nav>
        </div>

        {/* Right side — desktop CTAs OR mobile burger */}
        <div className="flex items-center gap-3 text-sm">
          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <span className="text-slate-700 text-sm font-bold hidden sm:inline">
                  Hola, {user.email.split('@')[0]} 👋
                </span>
                <Link to="/dashboard" className="btn-primary gap-2">
                  <LayoutDashboard size={15} />
                  Acceder a la app
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-secondary">Iniciar sesión</Link>
                <Link to="/register" className="btn-primary">Crear cuenta</Link>
              </>
            )}
          </div>

          {/* Mobile burger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileOpen}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <MobileSheet open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}

/* ── Mobile sheet (burger menu) ─────────────────────────────────────── */

function MobileSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  if (!open) return null;

  const linkCls =
    'block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-100 transition';

  return (
    <>
      {/* backdrop */}
      <button
        aria-label="Cerrar menú"
        onClick={onClose}
        className="lg:hidden fixed inset-x-0 top-16 bottom-0 bg-slate-900/40 z-30"
      />

      {/* panel */}
      <div className="lg:hidden fixed top-16 inset-x-0 z-40 bg-white border-b border-slate-200 shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
        <nav className="px-4 py-3 space-y-1">
          <a href="/#como-funciona" onClick={onClose} className={linkCls}>Cómo funciona</a>

          <details className="group rounded-lg open:bg-slate-50">
            <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Redes sociales
              <ChevronDown size={16} className="text-slate-400 transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <div className="grid grid-cols-2 gap-1.5 px-2 pb-3 pt-1">
              {NETWORKS.map((n) => (
                <Link
                  key={n.name}
                  to={n.to}
                  onClick={onClose}
                  className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-white transition"
                >
                  <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0">
                    <n.Icon size={15} color={n.color} />
                  </span>
                  <span className="text-sm font-medium text-slate-700 truncate">{n.name}</span>
                </Link>
              ))}
            </div>
          </details>

          <details className="group rounded-lg open:bg-slate-50">
            <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Características
              <ChevronDown size={16} className="text-slate-400 transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <div className="px-2 pb-3 pt-1 space-y-3">
              {getFeaturesByCategory().map(({ category, features }) =>
                features.length === 0 ? null : (
                  <div key={category.id}>
                    <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {category.label}
                    </p>
                    {features.map((f) => {
                      const Icon = f.icon;
                      return (
                        <Link
                          key={f.slug}
                          to={`/features/${f.slug}`}
                          onClick={onClose}
                          className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-white transition"
                        >
                          <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0 text-brand-700">
                            <Icon size={15} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-slate-700 truncate">
                              {f.title}
                            </span>
                            <span className="block text-[11px] text-slate-500 truncate">
                              {f.shortDescription}
                            </span>
                          </span>
                          {f.badge && (
                            <span className="shrink-0 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-500 to-pink-500 text-white">
                              {f.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                ),
              )}
              <Link
                to="/features"
                onClick={onClose}
                className="mt-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold text-brand-600 border-t border-slate-100 pt-3"
              >
                Ver todas las features
                <ArrowRight size={13} />
              </Link>
            </div>
          </details>
          <details className="group rounded-lg open:bg-slate-50">
            <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Herramientas gratis
              <ChevronDown size={16} className="text-slate-400 transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <div className="px-2 pb-3 pt-1">
              {TOOLS.map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.slug}
                    to={`/herramientas-gratuitas/${t.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-white transition"
                  >
                    <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0 text-brand-700">
                      <Icon size={15} />
                    </span>
                    <span className="block text-sm font-medium text-slate-700 truncate">{t.title}</span>
                  </Link>
                );
              })}
              <Link
                to="/herramientas-gratuitas"
                onClick={onClose}
                className="mt-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold text-brand-600 border-t border-slate-100 pt-3"
              >
                Ver todas las herramientas
                <ArrowRight size={13} />
              </Link>
            </div>
          </details>
          <a href="/#faqs" onClick={onClose} className={linkCls}>FAQs</a>
          <Link to="/precios" onClick={onClose} className={linkCls}>Precios</Link>
          <Link to="/blog" onClick={onClose} className={linkCls}>Blog</Link>
        </nav>

        {/* CTAs */}
        <div className="border-t border-slate-100 px-4 py-4 space-y-2">
          {user ? (
            <Link to="/dashboard" onClick={onClose} className="btn-primary w-full justify-center gap-2">
              <LayoutDashboard size={15} />
              Acceder a la app
            </Link>
          ) : (
            <>
              <Link to="/register" onClick={onClose} className="btn-primary w-full justify-center">
                Crear cuenta
              </Link>
              <Link to="/login" onClick={onClose} className="btn-secondary w-full justify-center">
                Iniciar sesión
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Social networks megamenu ───────────────────────────────────────── */

type NetworkLink = {
  Icon: IconType;
  name: string;
  /** Brand color for the icon. */
  color: string;
  /** Destination — set to the dedicated landing once it exists. */
  to: string;
  /** Short pitch shown below the network name. */
  hint: string;
};

/**
 * Every network we can publish to. `to` points at the dedicated landing when
 * it exists, otherwise at `/precios` (which lists the networks per plan).
 * As `/programar-{red}` landings are built, just update the `to` field here.
 */
const NETWORKS: NetworkLink[] = [
  { Icon: FaInstagram, name: 'Instagram',   color: '#E4405F', to: '/programar-instagram', hint: 'Posts, Reels y Stories' },
  { Icon: FaXTwitter,  name: 'X (Twitter)', color: '#0F1419', to: '/programar-x',         hint: 'Tweets con imagen o vídeo' },
  { Icon: FaLinkedin,  name: 'LinkedIn',    color: '#0A66C2', to: '/programar-linkedin',  hint: 'Posts y vídeo' },
  { Icon: FaFacebook,  name: 'Facebook',    color: '#1877F2', to: '/programar-facebook',  hint: 'Posts, Reels y Stories' },
  { Icon: FaTiktok,    name: 'TikTok',      color: '#000000', to: '/programar-tiktok',    hint: 'Vídeos verticales' },
  { Icon: FaYoutube,   name: 'YouTube',     color: '#FF0000', to: '/programar-youtube',   hint: 'Shorts y vídeo' },
  { Icon: FaThreads,   name: 'Threads',     color: '#000000', to: '/programar-threads',   hint: 'Posts e hilos' },
  { Icon: FaPinterest, name: 'Pinterest',   color: '#BD081C', to: '/programar-pinterest', hint: 'Pines visuales' },
  { Icon: FaBluesky,   name: 'Bluesky',     color: '#0085FF', to: '/programar-bluesky',   hint: 'Posts y respuestas' },
];

/**
 * Desktop megamenu listing every supported social network in a 3×3 grid. Same
 * hover-with-gap pattern as `BlogMenu` — the `pt-3` lives inside the group so
 * the panel survives moving the cursor down from the trigger.
 */
function SocialMenu() {
  return (
    <div className="relative group">
      <button
        type="button"
        className="text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1 font-medium"
      >
        Redes sociales
        <ChevronDown
          size={13}
          className="opacity-60 transition-transform duration-200 group-hover:rotate-180"
        />
      </button>

      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 z-50">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-5 w-[660px]">
          <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-4">
            Programa y publica en
          </p>
          <div className="grid grid-cols-3 gap-2">
            {NETWORKS.map((n) => (
              <Link
                key={n.name}
                to={n.to}
                className="group/card flex items-center gap-3 rounded-xl p-3 hover:bg-slate-50 transition"
              >
                <div className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 shrink-0 group-hover/card:bg-white group-hover/card:border-slate-200 transition">
                  <n.Icon size={18} color={n.color} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm leading-tight group-hover/card:text-brand-700 transition">
                    {n.name}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug truncate">
                    {n.hint}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 text-center">
            <Link
              to="/precios"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 transition"
            >
              Ver planes
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * "Blog" menu item with a desktop hover megamenu showing the latest 2 articles.
 * On mobile (`md:` not present) it falls back to a plain link — the wrapper is
 * inside a `hidden md:flex` nav, so the panel is only ever rendered on desktop.
 *
 * Hover is anchored on the outer `group` so moving the cursor through the
 * `pt-3` gap between the link and the panel keeps the menu open.
 */
function BlogMenu() {
  const { data, isLoading } = useQuery({
    queryKey: ['blog', 'latest-2'],
    queryFn: () => api<{ posts: BlogPostCard[] }>('/api/blog'),
    staleTime: 5 * 60_000,
  });
  const latest = (data?.posts ?? []).slice(0, 2);

  return (
    <div className="relative group">
      <Link
        to="/blog"
        className="text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1"
      >
        Blog
        <ChevronDown
          size={13}
          className="opacity-60 transition-transform duration-200 group-hover:rotate-180"
        />
      </Link>

      {/* Megamenu panel */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 z-50">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-5 w-[640px]">
          {isLoading || latest.length === 0 ? (
            <div className="py-10 flex items-center justify-center gap-2 text-slate-400 text-sm">
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Cargando últimos artículos…
                </>
              ) : (
                'Aún no hay artículos publicados.'
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {latest.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group/card block rounded-xl p-2 -m-2 hover:bg-slate-50 transition"
                >
                  <div className="aspect-[16/9] rounded-lg overflow-hidden bg-slate-100 mb-3">
                    {post.featuredImageUrl && (
                      <img
                        src={post.featuredImageUrl}
                        alt={post.featuredImageAlt || post.title}
                        loading="lazy"
                        className="h-full w-full object-cover group-hover/card:scale-[1.04] transition-transform duration-300"
                      />
                    )}
                  </div>
                  {post.category && (
                    <span className="text-[10px] font-semibold text-brand-600 uppercase tracking-wider">
                      {post.category}
                    </span>
                  )}
                  <h3 className="mt-1 font-bold text-slate-900 text-sm leading-snug line-clamp-2 group-hover/card:text-brand-700 transition">
                    {post.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-slate-100 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 transition"
            >
              Ver todos los artículos
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Desktop megamenu de "Herramientas gratis" — grid de 2 columnas con todas las
 * herramientas gratuitas (lib/tools). El trigger enlaza al hub.
 */
function ToolsMenu() {
  return (
    <div className="relative group">
      <Link
        to="/herramientas-gratuitas"
        className="text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1 font-medium"
      >
        Herramientas gratis
        <ChevronDown
          size={13}
          className="opacity-60 transition-transform duration-200 group-hover:rotate-180"
        />
      </Link>

      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 z-50">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-4 w-[680px] max-w-[calc(100vw-2rem)]">
          <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3 px-1">
            Gratis · sin registro
          </p>
          <div className="grid grid-cols-2 gap-x-5 gap-y-0">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.slug}
                  to={`/herramientas-gratuitas/${t.slug}`}
                  className="group/item flex items-start gap-2.5 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition"
                >
                  <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-slate-50 border border-slate-100 text-brand-700 shrink-0 group-hover/item:bg-white group-hover/item:border-slate-200 transition">
                    <Icon size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-bold text-slate-900 group-hover/item:text-brand-700 transition leading-tight">
                      {t.title}
                    </span>
                    <span className="block text-[11.5px] text-slate-500 leading-snug mt-0.5">
                      {t.shortDescription}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 text-center">
            <Link
              to="/herramientas-gratuitas"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 transition"
            >
              Ver todas las herramientas
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Desktop megamenu de "Características" — grid horizontal de 3 columnas con
 * todas las features. Cada item se ve completo (título + descripción de 2
 * líneas, sin truncar) gracias a que dejamos los items respirar a lo ancho.
 * Las categorías sólo viven en la página `/features`; en el menú prima la
 * escaneabilidad.
 */
function FeaturesMenu() {
  // Aplanamos manteniendo el orden por categoría definido en `features.ts`.
  const features = getFeaturesByCategory().flatMap((g) => g.features);

  return (
    <div className="relative group">
      <Link
        to="/features"
        className="text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1 font-medium"
      >
        Características
        <ChevronDown
          size={13}
          className="opacity-60 transition-transform duration-200 group-hover:rotate-180"
        />
      </Link>

      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 z-50">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-4 w-[1000px] max-w-[calc(100vw-2rem)]">
          <div className="grid grid-cols-3 gap-x-5 gap-y-0">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Link
                  key={f.slug}
                  to={`/features/${f.slug}`}
                  className="group/item flex items-start gap-2.5 rounded-lg px-2.5 py-1.5 -mx-0 hover:bg-slate-50 transition"
                >
                  <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-slate-50 border border-slate-100 text-brand-700 shrink-0 group-hover/item:bg-white group-hover/item:border-slate-200 transition">
                    <Icon size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[13px] font-bold text-slate-900 group-hover/item:text-brand-700 transition leading-tight">
                        {f.title}
                      </span>
                      {f.badge && (
                        <span className="shrink-0 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-500 to-pink-500 text-white">
                          {f.badge}
                        </span>
                      )}
                    </span>
                    <span className="block text-[11.5px] text-slate-500 leading-snug mt-0.5">
                      {f.shortDescription}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[11px] text-slate-400">
              Todas las características que hacen de Zettapost el todo en uno para publicar en redes sociales
            </p>
            <Link
              to="/features"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 transition"
            >
              Ver todas
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
